import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import CellTowerIcon from '@mui/icons-material/CellTower';
import NotesIcon from '@mui/icons-material/Notes';
import PlaceIcon from '@mui/icons-material/Place';
import RouteIcon from '@mui/icons-material/Route';
import {
  createPacket,
  findPacketsBySubject,
  getSubjectOptions,
  IntelPacket,
  MemberType,
  PacketSubjectOptions,
} from '@signals/app-shell/services/intelPacketApi';

type SubjectKind = 'geofence' | 'device' | 'mission' | 'blank';
type DeviceOption = PacketSubjectOptions['devices'][number];
type MissionOption = NonNullable<PacketSubjectOptions['missions']>[number];

interface CreatePacketDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (packet: IntelPacket) => void;
}

const KIND_CARDS: Array<{ id: SubjectKind; title: string; body: string; icon: React.ReactNode }> = [
  {
    id: 'geofence',
    title: 'Geofence',
    body: 'Collect DFP, OSINT, enter/exit, and presence around a fence.',
    icon: <PlaceIcon sx={{ fontSize: 22 }} />,
  },
  {
    id: 'device',
    title: 'Device',
    body: 'Build Pattern of Life, DFP, and fence crossings for one ID.',
    icon: <CellTowerIcon sx={{ fontSize: 22 }} />,
  },
  {
    id: 'mission',
    title: 'Mission',
    body: 'Open a file on a planned run and everything it touches.',
    icon: <RouteIcon sx={{ fontSize: 22 }} />,
  },
  {
    id: 'blank',
    title: 'Blank file',
    body: 'Name a draft and attach subjects later.',
    icon: <NotesIcon sx={{ fontSize: 22 }} />,
  },
];

const CreatePacketDialog: React.FC<CreatePacketDialogProps> = ({ open, onClose, onCreated }) => {
  const [kind, setKind] = useState<SubjectKind>('geofence');
  const [name, setName] = useState('');
  const [options, setOptions] = useState<PacketSubjectOptions>({ geofences: [], devices: [] });
  const [fenceId, setFenceId] = useState<number | null>(null);
  const [deviceId, setDeviceId] = useState('');
  const [deviceLabel, setDeviceLabel] = useState('');
  const [missionId, setMissionId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<IntelPacket[]>([]);

  useEffect(() => {
    if (!open) return;
    setKind('geofence');
    setName('');
    setFenceId(null);
    setDeviceId('');
    setDeviceLabel('');
    setMissionId(null);
    setError(null);
    setExisting([]);
    setLoading(true);
    getSubjectOptions()
      .then(setOptions)
      .catch(() => setOptions({ geofences: [], devices: [], missions: [] }))
      .finally(() => setLoading(false));
  }, [open]);

  const selectedFence = useMemo(
    () => options.geofences.find((g) => g.id === fenceId) || null,
    [options.geofences, fenceId],
  );

  /** A mission already on a packet cannot seed a second one. */
  const missionOptions = useMemo(
    () => (options.missions || []).filter((m) => !m.packet_id),
    [options.missions],
  );

  const selectedMission = useMemo(
    () => missionOptions.find((m) => m.id === missionId) || null,
    [missionOptions, missionId],
  );

  useEffect(() => {
    const run = async () => {
      if (kind === 'geofence' && fenceId) {
        setExisting(await findPacketsBySubject('geofence', String(fenceId)));
        return;
      }
      if (kind === 'device' && deviceId.trim()) {
        setExisting(await findPacketsBySubject('device', deviceId.trim()));
        return;
      }
      if (kind === 'mission' && missionId) {
        setExisting(await findPacketsBySubject('mission', String(missionId)));
        return;
      }
      setExisting([]);
    };
    run().catch(() => setExisting([]));
  }, [kind, fenceId, deviceId, missionId]);

  const suggestedName = useMemo(() => {
    if (kind === 'geofence' && selectedFence) return selectedFence.name;
    if (kind === 'device' && (deviceLabel || deviceId)) return deviceLabel || deviceId;
    if (kind === 'mission' && selectedMission) return selectedMission.name;
    return '';
  }, [kind, selectedFence, deviceLabel, deviceId, selectedMission]);

  const canSubmit = kind === 'blank'
    ? !!name.trim()
    : kind === 'geofence'
      ? !!fenceId
      : kind === 'mission'
        ? !!missionId
        : !!deviceId.trim();

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      let primary_subject_type: MemberType | undefined;
      let primary_subject_id: string | undefined;
      let primary_label: string | undefined;
      if (kind === 'geofence' && fenceId) {
        primary_subject_type = 'geofence';
        primary_subject_id = String(fenceId);
        primary_label = selectedFence?.name;
      } else if (kind === 'device' && deviceId.trim()) {
        primary_subject_type = 'device';
        primary_subject_id = deviceId.trim();
        primary_label = deviceLabel.trim() || deviceId.trim();
      } else if (kind === 'mission' && missionId) {
        primary_subject_type = 'mission';
        primary_subject_id = String(missionId);
        primary_label = selectedMission?.name;
      }
      const created = await createPacket({
        name: (name.trim() || suggestedName || 'Intel Packet').slice(0, 255),
        primary_subject_type,
        primary_subject_id,
        primary_label,
        start_collect: kind !== 'blank',
      });
      onCreated(created);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>New Intel Packet</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Start from a geofence, a device, or a mission. Collection begins immediately.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 2 }}>
          {KIND_CARDS.map((card) => {
            const active = kind === card.id;
            return (
              <Box
                key={card.id}
                onClick={() => setKind(card.id)}
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: active ? '#f5b942' : 'divider',
                  bgcolor: active ? 'rgba(245,185,66,0.10)' : 'transparent',
                  transition: 'border-color 120ms, background 120ms',
                  '&:hover': { borderColor: '#f5b942' },
                }}
              >
                <Box sx={{ color: active ? '#f5b942' : 'text.secondary', mb: 0.5 }}>{card.icon}</Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{card.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                  {card.body}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <>
            {kind === 'geofence' && (
              <Autocomplete
                options={options.geofences}
                getOptionLabel={(o) => o.name || String(o.id)}
                value={selectedFence}
                onChange={(_, v) => setFenceId(v ? v.id : null)}
                renderInput={(params) => <TextField {...params} size="small" label="Geofence" />}
                sx={{ mb: 1.5 }}
              />
            )}
            {kind === 'device' && (
              <Autocomplete<DeviceOption, false, false, true>
                freeSolo
                options={options.devices}
                getOptionLabel={(o) => (typeof o === 'string' ? o : `${o.label} · ${o.device_id}`)}
                onChange={(_, v) => {
                  if (typeof v === 'string') {
                    setDeviceId(v);
                    setDeviceLabel(v);
                  } else if (v) {
                    setDeviceId(v.device_id);
                    setDeviceLabel(v.label);
                  }
                }}
                onInputChange={(_, v, reason) => {
                  if (reason === 'input') {
                    setDeviceId(v);
                    setDeviceLabel(v);
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Device or entity ID" sx={{ mb: 1.5 }} />
                )}
              />
            )}
            {kind === 'mission' && (
              <>
                <Autocomplete<MissionOption>
                  options={missionOptions}
                  getOptionLabel={(o) => o.name}
                  value={selectedMission}
                  onChange={(_, v) => setMissionId(v ? v.id : null)}
                  noOptionsText="No unfiled missions. Plan one first, or unlink it from its packet."
                  renderInput={(params) => <TextField {...params} size="small" label="Mission" />}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {selectedMission
                    ? `Files ${selectedMission.geofence_count} watched area${selectedMission.geofence_count === 1 ? '' : 's'} `
                      + `and ${selectedMission.device_count} phone${selectedMission.device_count === 1 ? '' : 's'} as subjects, `
                      + 'and links the mission to the new packet.'
                    : 'The mission\u2019s watched areas and assigned phones become the packet\u2019s subjects.'}
                </Typography>
              </>
            )}
            <TextField
              fullWidth
              size="small"
              label="Packet name"
              value={name}
              placeholder={suggestedName || 'Name this file'}
              onChange={(e) => setName(e.target.value)}
            />
          </>
        )}

        {existing.length > 0 && (
          <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1, bgcolor: 'rgba(245,185,66,0.08)', border: '1px solid rgba(245,185,66,0.35)' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
              Already on file
            </Typography>
            {existing.slice(0, 3).map((p) => (
              <Button
                key={p.id}
                size="small"
                onClick={() => { onCreated(p); onClose(); }}
                sx={{ textTransform: 'none', mt: 0.5, display: 'block' }}
              >
                Open {p.name}
              </Button>
            ))}
          </Box>
        )}
        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={saving || !canSubmit}>
          {saving ? 'Opening file…' : kind === 'blank' ? 'Create draft' : 'Create and collect'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePacketDialog;
